import { Dispatch } from 'react';
import { RESET_VEHICLE, SET_DATA_MODEL_VALUE } from '../store/ActionKeys';
import { genericRequest, setSelectionValue } from '../store/Actions';
import { updatePackages } from './shared';
import {
    DMR,
    IAction,
    Model,
    Package,
    PackageInfo,
    ProductNames,
    Selection,
    VehicleQualifier
} from '../interfaces/interfaces';
import { IConfigProps } from '../context/ConfigContext';
import { apiGatewayBase, wso2header } from '../constants';
import { IUserContextProps } from '../context/UserContext';

// From carByBrand.manufacturer.filter in https://www.topdanmark.dk/rest/resourcebundle/dk.onsacar2020/
export const popularManufactors = [
    'Alfa Romeo',
    'Audi',
    'BMW',
    'Chevrolet',
    'Chrysler',
    'Citroën',
    'Dacia',
    'Daewoo',
    'DS',
    'Fiat',
    'Ford',
    'Honda',
    'Hyundai',
    'Jaguar',
    'Jeep',
    'Kia',
    'Land Rover',
    'Mazda',
    'Mercedes-Benz',
    'Mini',
    'Mitsubishi',
    'Nissan',
    'Opel',
    'Peugeot',
    'Porsche',
    'Renault',
    'Saab',
    'Seat',
    'Skoda',
    'Smart',
    'Subaru',
    'Suzuki',
    'Tesla',
    'Toyota',
    'Volvo',
    'VW',
    'Lexus',
    'Cupra',
    'Maxus',
    'MG',
    'Polestar',
    'Aiways',
    'Fisker',
    'NIO',
    'BYD',
    'Xpeng',
    'Hongqi',
    'JAC',
    'Voyah'
];

// These manufacturers must always lead to a RMO
export const excludedManufacturers = [
    'aston martin',
    'alpina',
    'bentley',
    'bugatti',
    'cisitalia,',
    'de tomaso',
    'delahaye',
    'delorean',
    'ferrari',
    'lamborghini',
    'maserati',
    'mclaren',
    'rolls-royce'
];

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/makes?type=BIL
export const getManufactors = async () => {
    const data: Array<any> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/makes?type=BIL`,
        headers: wso2header
    });
    const filtered = data.filter((x) => popularManufactors.includes(x.fabrikatNavn));
    const dropdownData: { label: string; value: string }[] = [];
    filtered.forEach((x) => {
        const e = {
            label: x.fabrikatNavn,
            value: '' + x.topFabrikatId
        };
        dropdownData.push(e);
    });
    return dropdownData;
};

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/fuels?type=BIL&plateType=P&topFabrikatId=6
export const getFuels = async (topFabrikatId: string) => {
    const data: Array<any> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/fuels?type=BIL&plateType=P&topFabrikatId=${topFabrikatId}`,
        headers: wso2header
    });
    const dropdownData: { label: string; value: string }[] = [];
    data.forEach((x) => {
        const e = {
            label: x.drivmiddelTxt,
            value: x.drivmiddelId
        };
        dropdownData.push(e);
    });
    return dropdownData;
};

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/models?plateType=P&topFabrikatId=12&fuel=B
export const getModels = async (topFabrikatId: string, fuel: string) => {
    const data: Array<any> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/models?plateType=P&topFabrikatId=${topFabrikatId}&fuel=${fuel}`,
        headers: wso2header
    });
    const dropdownData: { label: string; value: string }[] = [];
    data.forEach((x) => {
        const e = {
            label: x.modelNavn,
            value: '' + x.topModelId
        };
        dropdownData.push(e);
    });
    return dropdownData;
};

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/years?plateType=P&topModelId=1307&fuel=B
export const getYears = async (topModelId: string, fuel: string) => {
    const data: Array<any> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/years?plateType=P&topModelId=${topModelId}&fuel=${fuel}`,
        headers: wso2header
    });
    const dropdownData: { label: string; value: string }[] = [];
    data.forEach((x) => {
        const e = {
            label: x.aargangNysalg,
            value: x.aargangNysalg
        };
        dropdownData.push(e);
    });
    return dropdownData;
};

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/engines?plateType=P&topModelId=1189&fuel=B&year=2008
export const getEngines = async (topModelId: string, fuel: string, year: string) => {
    const data: Array<any> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/engines?plateType=P&topModelId=${topModelId}&fuel=${fuel}&year=${year}`,
        headers: wso2header
    });
    const dropdownData: { label: string; value: string }[] = [];
    data.forEach((x) => {
        const e = {
            label: x.motorstoerrelse,
            value: x.motorstoerrelse
        };
        dropdownData.push(e);
    });
    return dropdownData;
};

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/powers?plateType=P&topModelId=102&fuel=B&year=2022&engine=1,2
export const getPowers = async (topModelId: string, fuel: string, year: string, engine: string) => {
    const data: Array<any> = await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/powers?plateType=P&topModelId=${topModelId}&fuel=${fuel}&year=${year}&engine=${engine}`,
        headers: wso2header
    });
    const dropdownData: { label: string; value: string }[] = [];
    data.forEach((x) => {
        const e = {
            label: '' + x.maxEffHkBetegn,
            value: '' + x.maksimalEffektKw
        };
        dropdownData.push(e);
    });
    return dropdownData;
};

// https://api.webplatform-hybridapps-staging-01.topdanmark.cloud/proxy/v2/openapi/vehicle-model-register/variants?rulebasedVariantSelection=true&plateType=P&topModelId=924630176&fuel=B/EL&year=2012&engine=2,0&power=296
export const getVariants = async (topModelId: string, fuel: string, year: string, engine: string, power: string) => {
    return await genericRequest({
        method: 'GET',
        url: `${apiGatewayBase}/v2/openapi/vehicle-model-register/variants?rulebasedVariantSelection=true&plateType=P&topModelId=${topModelId}&fuel=${fuel}&year=${year}&engine=${engine}&power=${power}`,
        headers: wso2header
    });
};

export const getCarVerification = async ({
    config,
    dispatch,
    selection,
    user,
    logGetCarVerificationError
}: {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    selection: Selection;
    user: IUserContextProps;
    logGetCarVerificationError: (errorMessage: string) => void;
}) => {
    try {
        const cprNo = user.stakeholder?.cprNo ?? selection.cprNo;
        const queryParam = selection.registrationNumber
            ? `?regNr=${selection.registrationNumber}`
            : selection.viNumber
              ? '?regNr=ZNTGDAD' // Dummy call when viNumber is used
              : `?brandId=${selection.manufactor}&modelId=${selection.model}&year=${selection.vintage}`;
        const endpoint = user.loggedIn
            ? {
                  ...config.endpoint.carVerification,
                  url: `${config.endpoint.carVerification.url}${queryParam}`
              }
            : {
                  ...config.endpoint.carVerificationOpenApi,
                  url: `${config.endpoint.carVerificationOpenApi.url}${queryParam}&cprNr=${cprNo}`
              };
        const data = await genericRequest(endpoint);

        dispatch({
            type: SET_DATA_MODEL_VALUE,
            payload: {
                productKey: ProductNames.CAR,
                dataKey: 'status',
                value: data
            }
        });
    } catch (error: any) {
        logGetCarVerificationError(error.message);
    }
};

export const resetCarVerification = (dispatch: Dispatch<IAction>) => {
    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: ProductNames.CAR,
            dataKey: 'status',
            value: null
        }
    });
};

export const resetVehicle = ({ dispatch }: { dispatch: Dispatch<IAction> }) => {
    dispatch({
        type: RESET_VEHICLE,
        payload: {}
    });
};

export const setVehicle = ({
    dispatch,
    dmrData,
    selection
}: {
    dispatch: Dispatch<IAction>;
    dmrData: DMR;
    selection: Selection;
}) => {
    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: ProductNames.CAR,
            dataKey: 'dmr',
            value: dmrData
        }
    });
    //for tracking
    setSelectionValue(dispatch, selection, {
        key: 'manufacturerName',
        value: dmrData.manufacturerName
    });
    setSelectionValue(dispatch, selection, {
        key: 'modelName',
        value: dmrData.modelName
    });
    setSelectionValue(dispatch, selection, {
        key: 'variantTypeName',
        value: dmrData.variantTypeName
    });
};

/**
 * @param registrationOrViNumber // Registration or Vehicle Indentify Number (VIN)
 * @param qualifier // Can be REG or VIN
 * @returns
 */
export const getVehicle = async (registrationOrViNumber: string, qualifier: VehicleQualifier) => {
    const url = `${apiGatewayBase}/v2/openapi/onlinesales-services/api/car/info?vehicleQualifier=${qualifier}&vehicleId=${registrationOrViNumber}`;
    return await genericRequest({
        method: 'GET',
        url: url,
        headers: wso2header
    });
};

export const updateMileage = async ({
    config,
    dispatch,
    selection,
    model,
    user
}: {
    config: IConfigProps;
    dispatch: Dispatch<IAction>;
    selection: Selection;
    model: Model;
    user: IUserContextProps;
}) => {
    const payload = {
        brand: 'TD',
        quoteIds: model[ProductNames.CAR].packages?.packages.map((p: Package) => p.quoteId),
        yearlyMillage: selection.mileage
    };
    await genericRequest(config.endpoint.car, payload);

    await updatePackages(
        {
            config,
            dispatch,
            selection: selection,
            model,
            user
        },
        selection.packageInfo === PackageInfo.LIABILITY_ONLY ? { name: PackageInfo.LIABILITY_ONLY, value: true } : null
    );

    // Merge new prices with existing package data
    //mergeNewPrices({ config, dispatch, selection, model, data: getResponse });
};

export const setDmrTrackingUnitShown = ({
    shown,
    dispatch,
    dmrData
}: {
    shown: boolean;
    dispatch: Dispatch<IAction>;
    dmrData: DMR | null;
}) => {
    const payload = {
        ...dmrData,
        trackingUnitShown: shown
    };

    dispatch({
        type: SET_DATA_MODEL_VALUE,
        payload: {
            productKey: ProductNames.CAR,
            dataKey: 'dmr',
            value: payload
        }
    });
};

export const isEligibleForLeasing = (dateStr: string) => {
    const getYearDifference = (startDate: Date, endDate: Date) => {
        const millisecondsPerYear = 1000 * 60 * 60 * 24 * 365.25; // Approximate number of milliseconds in a year
        const timeDifference = endDate.getTime() - startDate.getTime();
        const yearDifference = timeDifference / millisecondsPerYear;

        return Math.floor(yearDifference);
    };

    const maxYear = 4;
    const today = new Date();
    const otherDate = new Date(dateStr);
    return getYearDifference(otherDate, today) < maxYear;
};
